import type { OrderEventDto } from '@ecom/contracts';
import type { MessageKey } from '@/i18n/messages';

/**
 * What a timeline line says beyond its name.
 *
 * The API returns each moment with the store's own details — a transition's
 * from and to, the reason someone typed, the carrier, the message that went
 * out, the amount refunded and how. The screen used to print the name alone
 * ("Status changed") beside an actor id, which answered neither "to what?"
 * nor "who?". Pure, so the wording is tested without rendering.
 */

type Translate = (key: MessageKey, values?: Record<string, string | number>) => string;

export interface TimelineLine {
  /** The moment's name. */
  title: string;
  /** The details, joined: "Processing → Shipped · SMSA · SMSA2901". */
  detail: string | null;
  /** Free text someone wrote — a reason, a note — shown as its own line. */
  quote: string | null;
  /** Who acted, as a person reads it. */
  actor: string;
}

export interface TimelineHelpers {
  t: Translate;
  money: (halalas: number) => string;
  status: (status: string) => string;
  returnStatus: (status: string) => string;
}

export function describeEvent(event: OrderEventDto, helpers: TimelineHelpers): TimelineLine {
  const { t, money } = helpers;
  const data = event.data ?? {};
  const text = (key: string): string | null => (typeof data[key] === 'string' && data[key] !== '' ? (data[key] as string) : null);
  const amount = (key: string): number | null => (typeof data[key] === 'number' ? (data[key] as number) : null);
  const parts: string[] = [];
  let quote: string | null = null;

  switch (event.type) {
    case 'order.status_changed': {
      const from = text('from');
      const to = text('to');
      if (from && to) parts.push(`${helpers.status(from)} → ${helpers.status(to)}`);
      else if (to) parts.push(t('timelineTo', { to: helpers.status(to) }));
      const carrier = text('carrier');
      if (carrier) parts.push(text('trackingNumber') ? t('timelineCarrier', { carrier, number: text('trackingNumber') ?? '' }) : carrier);
      if (data.restocked === true) parts.push(t('timelineRestocked'));
      quote = text('reason');
      break;
    }
    case 'order.placed': {
      if (text('source') === 'admin') parts.push(t('timelinePlacedByStaff'));
      break;
    }
    case 'digital.delivered': {
      // Files and codes handed over on payment, or by "Deliver now" (ADR-077).
      if (amount('lines') !== null) parts.push(t('timelineDigitalLines', { count: String(amount('lines')) }));
      break;
    }
    case 'digital.short': {
      // A code pool ran dry: what is waiting, and how many codes it needs.
      parts.push(t('timelineDigitalShort', { product: text('product') ?? '', missing: String(amount('missing') ?? 0) }));
      break;
    }
    case 'order.assigned': {
      // Who has it now, and who had it (ADR-074).
      const to = text('to');
      const from = text('from');
      parts.push(to ? t('timelineAssignedTo', { name: to }) : t('timelineUnassigned'));
      if (from) parts.push(t('timelineAssignedFrom', { name: from }));
      break;
    }
    case 'order.edited': {
      const before = Array.isArray(data.before) ? (data.before as string[]).join(', ') : null;
      const after = Array.isArray(data.after) ? (data.after as string[]).join(', ') : null;
      if (before && after) parts.push(t('timelineEditedItems', { from: before, to: after }));
      const from = amount('fromTotal');
      const to = amount('toTotal');
      if (from !== null && to !== null && from !== to) parts.push(`${money(from)} → ${money(to)}`);
      if (text('shippingMethod')) parts.push(text('shippingMethod') ?? '');
      if (Array.isArray(data.changed)) {
        parts.push(t('timelineEditedFields', { fields: (data.changed as string[]).map((field) => t(`editField_${field}` as MessageKey)).join(', ') }));
      }
      quote = text('reason');
      break;
    }
    case 'payment.from_balance': {
      // The store credit that paid part or all of it (ADR-081).
      const paid = amount('amount');
      if (paid !== null) parts.push(money(paid));
      break;
    }
    case 'payment.succeeded': {
      const paid = amount('amount');
      if (text('provider') === 'manual' && paid !== null) {
        parts.push(t('timelinePaidOutside', { method: t(`refundMethod${text('method') ?? 'OTHER'}` as MessageKey), amount: money(paid) }));
        if (text('reference')) parts.push(text('reference') ?? '');
      } else if (data.collected === true && paid !== null) parts.push(t('timelineCashCollected', { amount: money(paid) }));
      else if (data.byHand === true && paid !== null) parts.push(t('timelinePaidByHand', { amount: money(paid) }));
      else if (text('provider')) parts.push(text('provider') ?? '');
      quote = text('reason');
      break;
    }
    case 'payment.refunded':
    case 'payment.refund_failed':
    case 'payment.refund_unconfirmed': {
      const refunded = amount('amount');
      if (refunded !== null) parts.push(t('timelineRefund', { amount: money(refunded) }));
      const number = text('returnNumber');
      if (number) parts.push(t('timelineRefundFor', { number }));
      const settlement = data.settlement as { method?: string; reference?: string } | undefined;
      if (settlement?.method) {
        parts.push(t('timelineSettledBy', { method: t(`refundMethod${settlement.method}` as MessageKey) }));
        if (settlement.reference) parts.push(settlement.reference);
      }
      quote = text('reason') ?? text('why');
      break;
    }
    case 'return.requested':
    case 'return.status_changed': {
      const number = text('returnNumber');
      const to = text('to');
      if (number) parts.push(number);
      if (to) parts.push(helpers.returnStatus(to));
      else if (text('sku')) parts.push(`${text('sku')} × ${amount('quantity') ?? 1}`);
      break;
    }
    case 'email.sent':
    case 'email.failed': {
      const template = text('template');
      if (template) parts.push(t(`mail.${template}` as MessageKey));
      if (text('to')) parts.push(text('to') ?? '');
      break;
    }
    case 'sms.sent': {
      if (text('to')) parts.push(text('to') ?? '');
      break;
    }
    case 'order.note': {
      quote = text('note');
      break;
    }
    default:
      break;
  }

  return {
    title: t(`event.${event.type}` as MessageKey),
    detail: parts.length > 0 ? parts.join(' · ') : null,
    quote,
    actor: actorOf(event, t),
  };
}

/** A name when the API knows one; otherwise what kind of actor it was. */
function actorOf(event: OrderEventDto, t: Translate): string {
  if (event.actorName) return event.actorName;
  if (event.actor === 'system') return t('actorSystem');
  if (event.actor === 'webhook') return t('actorWebhook');
  if (event.actor === 'guest') return t('actorGuest');
  return t('actorUnknown');
}
